// Angular 13 / TypeScript 4.5 cannot consume the modern Angular WebMCP providers
// or webmcp-types (which uses newer TypeScript syntax). Keep this narrow adapter
// aligned with the Document API; replace it with Angular's provider on upgrade.
export interface ParkingWebMcpTool {
    name: string
    description: string
    inputSchema: object
    annotations: { readOnlyHint: boolean; untrustedContentHint: boolean }
    execute(input: Record<string, unknown>, options: { signal: AbortSignal }): Promise<unknown>
}

export interface ParkingModelContext {
    registerTool(tool: ParkingWebMcpTool, options: { signal: AbortSignal }): Promise<void> | void
}

const TOOL_NAMES = new Set(['search_parking', 'get_parking_details', 'get_parking_by_city', 'get_parking_by_postal_code',
    'get_parking_by_features', 'list_parking_features', 'list_parking_cities'])

export function isParkingDiscoveryRoute(url: string): boolean {
    const parsed = new URL(url, 'https://parkupp.co.za')
    const enterprise = parsed.searchParams.get('enterprise')
    if (enterprise && enterprise !== 'parkupp') return false
    return parsed.pathname === '/' || /^\/map-search\/?$/.test(parsed.pathname) || /^\/parking\/[^/]+\/?$/.test(parsed.pathname)
}

export async function registerParkingWebMcp(
    context: ParkingModelContext,
    apiBase: string,
    lifetime: AbortSignal,
    fetcher: typeof fetch = fetch
): Promise<void> {
    async function request(path: string, input?: { name: string; arguments: Record<string, unknown> }, execution?: AbortSignal): Promise<any> {
        const controller = new AbortController()
        const abort = () => controller.abort()
        lifetime.addEventListener('abort', abort, { once: true })
        execution?.addEventListener('abort', abort, { once: true })
        const timer = setTimeout(abort, 25000)
        try {
            if (lifetime.aborted || execution?.aborted) controller.abort()
            const response = await fetcher(`${apiBase}${path}`, {
                method: input ? 'POST' : 'GET', credentials: 'omit', signal: controller.signal,
                headers: input ? { 'Content-Type': 'application/json' } : {},
                ...(input ? { body: JSON.stringify(input) } : {}),
            })
            const body = await response.json()
            if (!response.ok) return { error: typeof body?.error === 'string' ? body.error : 'Parking service unavailable. Please try again.' }
            return body
        } catch (error) {
            if (lifetime.aborted || execution?.aborted) throw error
            return { error: 'Parking service unavailable. Please try again or use the map search.' }
        } finally {
            clearTimeout(timer)
            lifetime.removeEventListener('abort', abort)
            execution?.removeEventListener('abort', abort)
        }
    }

    const manifest = await request('/tools')
    if (!Array.isArray(manifest?.tools)) throw new Error('Parking tools could not be loaded.')
    for (const tool of manifest.tools) {
        if (lifetime.aborted) return
        if (!TOOL_NAMES.has(tool.name) || typeof tool.description !== 'string' || !tool.inputSchema || typeof tool.inputSchema !== 'object') continue
        await context.registerTool({
            name: tool.name, description: tool.description, inputSchema: tool.inputSchema,
            annotations: { readOnlyHint: true, untrustedContentHint: true },
            execute: (input, { signal }) => request('/call', { name: tool.name, arguments: input }, signal),
        }, { signal: lifetime })
    }
}
